import React from 'react'
import PropTypes from 'prop-types'
import { graphql, withApollo } from 'react-apollo'

import { redirectToRegForAllIncompletes } from 'constants/feature-keys'
import { kycInReopen } from 'constants/feature-keys'

import { withToggles } from '@acorns/web-toggles-react'
import {
  withAnalyticsClient,
  withEnv,
  withRemoteStrings,
} from '@acorns/web-utils'
import { ErrorResponse } from 'apollo-link-error'
import gql from 'graphql-tag'
import {
  branch,
  compose,
  createSink,
  getContext,
  renderComponent,
  withContext,
  withPropsOnChange,
} from 'recompose'

import { routes } from 'router/routes'
import { initializeBrazeSession } from 'utils/braze'
import { resolveTokenFromStorage } from 'utils/session'
import {
  Props as AsyncComponentProps,
  withAsyncComponent,
} from 'utils/withAsyncComponent'

import { isSpendCardLocked } from '../isSpendCardLocked'
import { prefetchPresentScreenQueries } from './preload'
import * as preloadQueries from './preload/queries'
import { resolveOperationNames } from './utils'

interface IConfig {
  closedAccount: any
  error: any
  loading: any
  lockedSpendAccount: any
  redirectToMobileApp: any
  pricingInterstitial: any
}

export const QUERY = gql`
  query IsAuthenticated {
    user {
      closedAt
      email
      firstName
      lastName
      registrationComplete
      uuid
      verified
    }
  }
`

const SPEND_STATUS = gql`
  query SpendAccountStatus {
    allBankAccounts(input: { type: spend }) {
      createdAt
      availableBalance {
        currency
        value
      }
      id
    }
  }
`

const SUBSCRIPTION_QUERY = gql`
  query SubscriptionQuery {
    tierSubscriptionByUserId {
      id
      __typename
    }
  }
`

const SIMPLE_REG_INVESTMENT_ACCOUNT_CHECK_QUERY = gql`
  query SimpleRegInvestmentAccountCheckQuery {
    investmentAccountsByUserId(filter: { product: INVEST }) {
      id
      closedAt
    }
  }
`

export const hasUnauthenticatedError = (
  errors: ErrorResponse['graphQLErrors'] = [],
): boolean =>
  Boolean(
    errors.find(
      ({ name, message }) =>
        name === 'NotAuthorized' ||
        name === 'NotAuthorizedError' ||
        name === 'MissingTokenError' ||
        message === 'You do not have permission to access this resource.',
    ),
  )

export const hasServiceUnavailableError = (
  errors: readonly any[] = [],
): boolean => Boolean(errors.find(({ code }) => code === 'service_unavailable'))

const RedirectToSignup = createSink(({ env }: any) => {
  const { search } = window.location
  const url = new URL(env.get('registrationUrl'))

  if (search) {
    url.search = search
  }

  window.location.href = url.toString()
})

// Note: This allows consumers to pull from
// the global `auth` context
export const withAuth = getContext({
  auth: PropTypes.shape({
    uuid: PropTypes.string,
    token: PropTypes.string,
  }),
})

// Unauthenticated error modal will not display for these operations as they are made in parallel with the auth query. The auth query will handle the redirect.
export const OPERATIONS = resolveOperationNames([
  ...Object.values(preloadQueries),
  QUERY,
  SPEND_STATUS,
  SUBSCRIPTION_QUERY,
])

const CLOSED_ACCOUNT_ACCESSIBLE_PATHS = [
  routes.documents.index,
  routes.documents.documentUploaderIndex,
  routes.invest.fundIndex,
  routes.portfolio.aboutInvestPortfolio,
]

export default (config: IConfig) =>
  compose<AsyncComponentProps, {}>(
    withRemoteStrings,
    withAnalyticsClient,
    withEnv,
    withApollo,
    withToggles,
    graphql(QUERY, {
      name: 'authData',
      options: {
        notifyOnNetworkStatusChange: true,
        errorPolicy: 'all',
      },
    }),
    graphql(SPEND_STATUS, {
      name: 'spendAccountStatus',
      options: {
        notifyOnNetworkStatusChange: true,
        errorPolicy: 'all',
      },
    }),
    graphql(SUBSCRIPTION_QUERY, {
      name: 'subscriptionData',
      options: {
        fetchPolicy: 'network-only',
      },
    }),
    graphql(SIMPLE_REG_INVESTMENT_ACCOUNT_CHECK_QUERY, {
      name: 'simpleRegData',
    }),
    prefetchPresentScreenQueries,
    // Starts loading the authenticated pages bundle while auth requests are in flight
    withAsyncComponent({
      loading: () => config.loading,
      resolver: async () => {
        const { AuthenticatedRouter } = await import(
          /* webpackChunkName: "auth" */ '../../router/AuthenticatedRouter'
        )

        return AuthenticatedRouter
      },
    }),
    withPropsOnChange<any, any>(
      ['authData', 'spendAccountStatus'],
      ({ authData, analytics, spendAccountStatus }) => {
        if (authData.user) {
          // Note: Identify the user once for analytic events
          analytics.identify(authData.user.uuid, {
            userId: authData.user.uuid,
          })

          initializeBrazeSession(authData.user.uuid)
        }

        return {
          spendCardLocked: isSpendCardLocked(spendAccountStatus),
        }
      },
    ),
    // Note: Render a `loading` component while we are waiting
    branch<any>(
      ({ authData, subscriptionData, simpleRegData, remoteStringsState }) =>
        authData.loading ||
        !authData.user ||
        subscriptionData.loading ||
        remoteStringsState?.isLoadingRemoteStrings ||
        simpleRegData.loading,
      renderComponent(config.loading),
    ),
    // Note: Render the registration page if user hasn't completed registration
    // We want to make sure this is the very first item in the list of branches
    // so that we don't allow any non-registration complete users to enter the app
    branch<any>(
      ({ authData, toggles }) =>
        toggles.isFeatureEnabled(redirectToRegForAllIncompletes) &&
        !authData.user.registrationComplete,
      renderComponent(RedirectToSignup),
    ),
    // #inct-1840 https://acorns.atlassian.net/browse/GRWTH-10844
    // We're still unsure of every single case where an account could be marked as registration
    // complete without a subscription but this check is being done to force picking
    // a tier before having access to the app
    branch<any>(
      ({ authData, subscriptionData }) =>
        authData.user.registrationComplete &&
        !subscriptionData.tierSubscriptionByUserId,
      renderComponent(config.pricingInterstitial),
    ),
    // Note: Render the closed account page if the user has a closed account.
    // If user has closed account, they can still access the paths listed in CLOSED_ACCOUNT_ACCESSIBLE_PATHS.
    branch<any>(
      ({ authData }) =>
        authData.user &&
        authData.user.closedAt &&
        !CLOSED_ACCOUNT_ACCESSIBLE_PATHS.some((path) =>
          window.location.pathname.startsWith(path),
        ),
      renderComponent((props: any) => {
        const ClosedAccountComponent = config.closedAccount
        const { authData, toggles } = props
        const longReopen =
          toggles?.isFeatureEnabled(kycInReopen) && !authData.user.verified
        return <ClosedAccountComponent {...props} isLongReopen={longReopen} />
      }),
    ),
    // Note: Render the registration page if user hasn't completed registration
    branch<any>(
      ({ authData, toggles }) =>
        !toggles.isFeatureEnabled(redirectToRegForAllIncompletes) &&
        !authData.user.registrationComplete,
      renderComponent(RedirectToSignup),
    ),
    // Note: Renders Spend Locked for Fraud Component if true
    branch<any>(
      ({ spendCardLocked }) => spendCardLocked,
      renderComponent(config.lockedSpendAccount),
    ),
    // Note: Render a generic error component if we get any other type of error
    branch<any>(
      ({ authData }) => authData.error,
      renderComponent(config.error),
    ),

    // Note: Make `authData` available to all children
    withContext<any, any>(
      {
        auth: PropTypes.shape({
          uuid: PropTypes.string,
        }),
      },
      ({ authData }) => {
        if (authData.user) {
          const { uuid } = authData.user

          const token = resolveTokenFromStorage()

          return {
            auth: { uuid, token },
          }
        }
        return { auth: null }
      },
    ),
  )
